import React from 'react';

import { Alert, Descriptions, Modal, Spin, Table, Tabs, Tag } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useGetUserProfileQuery } from '../api';

interface Props {
  userId: number;
  onClose: () => void;
}

export function UserProfileModal({ userId, onClose }: Props) {
  const { t } = useTranslation();
  const { data, isFetching, isError } = useGetUserProfileQuery(userId);

  return (
    <Modal open title={t('admin-extensions.profile')} onCancel={onClose} footer={null} width={760} destroyOnClose>
      {isFetching && <Spin />}
      {isError && <Alert type="error" showIcon message={t('admin-extensions.load-error')} />}
      {data && (
        <>
          <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
            <Descriptions.Item label={t('table.fio')}>{data.user.name}</Descriptions.Item>
            <Descriptions.Item label={t('table.balance')}>{data.user.balance} ₽</Descriptions.Item>
            <Descriptions.Item label={t('admin-extensions.deposits')}>{data.deposits_total} ₽</Descriptions.Item>
            <Descriptions.Item label={t('admin-extensions.court-charges')}>{data.booking_charges_total} ₽</Descriptions.Item>
            <Descriptions.Item label={t('admin-extensions.group-charges')}>{data.group_charges_total} ₽</Descriptions.Item>
            <Descriptions.Item label={t('admin-extensions.refunds')}>{data.booking_refunds_total} ₽</Descriptions.Item>
          </Descriptions>
          <Tabs items={[
            {
              key: 'transactions', label: t('admin-extensions.transactions'), children: (
                <Table size="small" rowKey="id" dataSource={data.transactions} pagination={{ pageSize: 8 }}>
                  <Table.Column title={t('admin-extensions.date')} dataIndex="created" render={(value: string) => dayjs(value).format('DD.MM.YYYY HH:mm')} />
                  <Table.Column title={t('admin-extensions.operation')} dataIndex="type" render={(value: string) => t(`admin-extensions.transaction-${value}`, value)} />
                  <Table.Column title={t('admin-extensions.amount')} dataIndex="amount" render={(value: string) => `${value} ₽`} />
                </Table>
              )
            },
            {
              key: 'equipment', label: t('admin-extensions.equipment'), children: (
                <Table size="small" rowKey={(item) => `${item.booking_id}-${item.name}`} dataSource={data.extra_options} pagination={{ pageSize: 8 }}>
                  <Table.Column title={t('admin-extensions.date')} dataIndex="start_time" render={(value: string) => dayjs(value).format('DD.MM.YYYY')} />
                  <Table.Column title={t('admin-extensions.item')} dataIndex="name" />
                  <Table.Column title={t('admin-extensions.quantity')} dataIndex="amount" />
                  <Table.Column title={t('admin-extensions.price')} dataIndex="unit_price" render={(value: string) => `${value} ₽`} />
                  <Table.Column title={t('admin-extensions.status')} dataIndex="confirmed" render={(value: boolean) => <Tag color={value ? 'green' : 'default'}>{t(value ? 'admin-extensions.confirmed' : 'admin-extensions.unconfirmed')}</Tag>} />
                </Table>
              )
            },
            {
              key: 'subscriptions', label: t('admin-extensions.subscriptions'), children: (
                <Table size="small" rowKey="id" dataSource={data.subscriptions} pagination={{ pageSize: 8 }}>
                  <Table.Column title={t('admin-extensions.name')} dataIndex="name" />
                  <Table.Column title={t('admin-extensions.remaining')} dataIndex="available_lessons" />
                  <Table.Column title={t('admin-extensions.expires')} dataIndex="expires_on" render={(value: string | null) => value ? dayjs(value).format('DD.MM.YYYY') : t('admin-extensions.no-expiry')} />
                </Table>
              )
            },
            {
              key: 'debts', label: `${t('admin-extensions.debts')} (${data.lesson_debts.length})`, children: (
                <Table size="small" rowKey="id" dataSource={data.lesson_debts} pagination={{ pageSize: 8 }}>
                  <Table.Column title={t('admin-extensions.date')} dataIndex="start_time" render={(value: string) => dayjs(value).format('DD.MM.YYYY HH:mm')} />
                  <Table.Column title={t('admin-extensions.booking')} dataIndex="booking_id" />
                </Table>
              )
            },
          ]}
          />
        </>
      )}
    </Modal>
  );
}
